import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Target, Save, Activity, Award, Users, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface KPI {
  id: string;
  name: string;
  description: string;
  category: string;
  metric_type: string;
  target_value: number;
  measurement_frequency: string;
}

interface KPIValueRecorderProps {
  meetingId: string;
  onClose: () => void;
}

export function KPIValueRecorder({ meetingId, onClose }: KPIValueRecorderProps) {
  const { user } = useAuth();
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [selectedKPI, setSelectedKPI] = useState<string>('');
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadKPIs();
  }, []);

  const loadKPIs = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      let query = supabase
        .from('district_kpis')
        .select('*')
        .eq('is_active', true)
        .order('category');

      if (profile?.district_id) {
        query = query.eq('district_id', profile.district_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setKpis(data || []);
    } catch (error) {
      console.error('Error loading KPIs:', error);
      toast.error('Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedKPI || !value) {
      toast.error('Please select a KPI and enter a value');
      return;
    }

    setSaving(true);
    try {
      // Here we would typically save to a meeting_kpi_values table
      // For now, we'll add it to the meeting's meeting_data field
      const { data: meeting, error: fetchError } = await supabase
        .from('meetings')
        .select('meeting_data')
        .eq('id', meetingId)
        .single();

      if (fetchError) throw fetchError;

      const meetingData = (meeting?.meeting_data as any) || {};
      if (!meetingData.kpi_values) {
        meetingData.kpi_values = [];
      }

      // Add or update KPI value
      const existingIndex = meetingData.kpi_values.findIndex(
        (kv: any) => kv.kpi_id === selectedKPI
      );

      const kpiValue = {
        kpi_id: selectedKPI,
        value: parseFloat(value),
        notes: notes,
        recorded_at: new Date().toISOString(),
        recorded_by: user?.id
      };

      if (existingIndex >= 0) {
        meetingData.kpi_values[existingIndex] = kpiValue;
      } else {
        meetingData.kpi_values.push(kpiValue);
      }

      const { error: updateError } = await supabase
        .from('meetings')
        .update({ meeting_data: meetingData })
        .eq('id', meetingId);

      if (updateError) throw updateError;

      toast.success('KPI value recorded successfully');
      onClose();
    } catch (error) {
      console.error('Error saving KPI value:', error);
      toast.error('Failed to record KPI value');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      performance: Activity,
      satisfaction: Award,
      engagement: Users,
      effectiveness: TrendingUp
    };
    return icons[category as keyof typeof icons] || Target;
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      performance: 'bg-blue-100 text-blue-800',
      satisfaction: 'bg-green-100 text-green-800',
      engagement: 'bg-purple-100 text-purple-800',
      effectiveness: 'bg-orange-100 text-orange-800'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInputType = (metricType: string) => {
    switch (metricType) {
      case 'percentage':
        return { type: 'number', min: '0', max: '100', step: '0.1' };
      case 'scale':
        return { type: 'number', min: '1', max: '5', step: '0.1' };
      case 'boolean':
        return { type: 'number', min: '0', max: '1', step: '1' };
      default:
        return { type: 'number', step: '0.1' };
    }
  };

  const selectedKPIData = kpis.find(k => k.id === selectedKPI);

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent>
          <div className="text-center py-8">Loading KPIs...</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Record KPI Value
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Select KPI</Label>
            <Select value={selectedKPI} onValueChange={setSelectedKPI}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a KPI to record" />
              </SelectTrigger>
              <SelectContent>
                {kpis.map((kpi) => {
                  const CategoryIcon = getCategoryIcon(kpi.category);
                  return (
                    <SelectItem key={kpi.id} value={kpi.id}>
                      <div className="flex items-center gap-2">
                        <CategoryIcon className="h-4 w-4" />
                        <span>{kpi.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {kpi.category}
                        </Badge>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {selectedKPIData && (
            <Card className="p-4 bg-muted/50">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className={getCategoryColor(selectedKPIData.category)}>
                    {selectedKPIData.category}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Target: {selectedKPIData.target_value} ({selectedKPIData.metric_type})
                  </span>
                </div>
                <p className="text-sm">{selectedKPIData.description}</p>
              </div>
            </Card>
          )}

          <div>
            <Label htmlFor="value">
              Value *
              {selectedKPIData?.metric_type === 'percentage' && ' (0-100%)'}
              {selectedKPIData?.metric_type === 'scale' && ' (1-5 scale)'}
              {selectedKPIData?.metric_type === 'boolean' && ' (0 = No, 1 = Yes)'}
            </Label>
            <Input
              id="value"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={
                selectedKPIData?.metric_type === 'percentage' ? '85.5' :
                selectedKPIData?.metric_type === 'scale' ? '4.2' :
                selectedKPIData?.metric_type === 'boolean' ? '1' : '10'
              }
              {...(selectedKPIData ? getInputType(selectedKPIData.metric_type) : { type: 'number' })}
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional context or observations about this KPI measurement..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || !selectedKPI || !value}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Recording...' : 'Record Value'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function KPIRecordButton({ meetingId }: { meetingId: string }) {
  const [showRecorder, setShowRecorder] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowRecorder(true)}
        className="flex items-center gap-2"
      >
        <Target className="h-4 w-4" />
        Record KPI
      </Button>

      {showRecorder && (
        <KPIValueRecorder
          meetingId={meetingId}
          onClose={() => setShowRecorder(false)}
        />
      )}
    </>
  );
}